<template>
  <div v-if="building" class="layout">
    <BuildingStats class="stats" :units="units" :statistics="statistics" />

    <BuildingPanel class="panel" :building="building" :collapsible="!screenMinLg" />

    <View direction="column" gap="md" class="units">
      <View v-if="status === 'pending'" justify="center" padding="xl">
        <LoadingIndicator />
      </View>

      <EmptyPagePlaceholder
        v-else-if="status === 'error'"
        title="Could not load the units"
        description="Please reload the page or try again later"
      />

      <EmptyState
        v-else-if="!units.length"
        icon="building-06"
        title="No units yet"
        description="Each unit is listed on its own, like any property, on the building's address."
      >
        <template #action>
          <View gap="sm" wrap="wrap" justify="center">
            <Button variant="outline" size="medium" data-testid="attach-first-home" @click="unitActions?.attachHomes()">
              <Icon name="home-03" size="small" />
              <span>Attach existing home</span>
            </Button>
            <Button size="medium" data-testid="add-first-unit" @click="unitActions?.addUnit()">
              <Icon name="plus" size="small" />
              <span>Add unit</span>
            </Button>
          </View>
        </template>
      </EmptyState>

      <template v-else>
        <!-- The properties page's tools: the status menu, then the sort and the cards/table switch -->
        <View gap="sm" align="center" justify="space-between" wrap="wrap" class="tools">
          <FilterMenu
            v-model="statusFilter"
            label="Status"
            :options="statusOptions"
            :full-width="screenMobile"
            mobile-sheet
            data-testid="unit-status-filter"
          />

          <View gap="sm" align="center">
            <MenuButton :button-label="`Sort: ${sort}`" size="medium" mobile-sheet sheet-title="Sort units">
              <MenuButtonItem
                v-for="option in sortOptions"
                :key="option"
                :title="option"
                :is-selected="option === sort"
                @click="sort = option"
              />
            </MenuButton>
            <ListViewToggle list="building-units" />
          </View>
        </View>

        <EmptyPagePlaceholder
          v-if="!shownUnits.length"
          title="No units found"
          description="Adjust your filters to find what you're looking for"
        />

        <!-- Inside its building a unit goes by its number; the address is the building's and sits in the header -->
        <PropertyTable
          v-else-if="showTable"
          :properties="shownUnits"
          :title-of="unitLabel"
          first-header="Unit"
          :show-portfolio="false"
          :show-manager="false"
        />

        <div v-else class="unit-cards">
          <PropertyCard v-for="unit in shownUnits" :key="unit.id" :property="unit" :title="unitLabel(unit)" />
        </div>
      </template>
    </View>
  </div>

  <View v-else-if="buildingStatus === 'pending'" justify="center" padding="xl">
    <LoadingIndicator />
  </View>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { useRoute } from 'vue-router';

import {
  Button,
  EmptyPagePlaceholder,
  EmptyState,
  FilterMenu,
  FilterMenuOption,
  Icon,
  LoadingIndicator,
  MenuButton,
  MenuButtonItem,
  View,
  screenMinLg,
  screenMobile,
} from '@tallo/design-system';
import {
  ListViewToggle,
  PropertyCard,
  PropertyTable,
  UnitSort,
  countByStatus,
  sortUnits,
  unitLabel,
  useBuildingQuery,
  useBuildingStatisticsQuery,
  useBuildingUnitsQuery,
  useListView,
} from '@tallo/investor/property';
import { PropertyStatus } from '@tallo/property';

import BuildingPanel from '../components/building-panel.vue';
import BuildingStats from '../components/building-stats.vue';
import { unitActionsKey } from '../unit-actions.key';

/** Every status, or one of them */
const allStatuses = 'All';

const route = useRoute();
// Read from the route every time: moving from one building's page to another's keeps this component
const buildingId = computed(() => route.params.buildingId as string);
const { data: building, status: buildingStatus } = useBuildingQuery(() => buildingId.value);
const { units, status } = useBuildingUnitsQuery(() => buildingId.value);
const { data: statistics } = useBuildingStatisticsQuery(() => buildingId.value);
const { showTable } = useListView('building-units');
const unitActions = inject(unitActionsKey);

const statusFilter = ref<string>(allStatuses);
const sort = ref(UnitSort.UNIT_NUMBER);
const sortOptions = Object.values(UnitSort);

// All, then only the statuses the building's units are in, in the order a listing moves through them
const statusOptions = computed<FilterMenuOption[]>(() => {
  const counts = countByStatus(units.value);

  return [
    { value: allStatuses, label: 'All', count: units.value.length },
    ...Object.values(PropertyStatus)
      .filter((status) => counts[status])
      .map((status) => ({ value: status, label: status, count: counts[status] })),
  ];
});

const shownUnits = computed(() => {
  const matching =
    statusFilter.value === allStatuses ? units.value : units.value.filter((unit) => unit.status === statusFilter.value);

  return sortUnits(matching, sort.value);
});
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

// The units beside a panel about the building. Below the desktop width the panel folds in under
// the stats: beside the panel the stats strip would have under 500px, where a one-word stat label
// no longer fits its column and the strip spills out of its card
.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 22rem;
  grid-template-areas:
    'stats panel'
    'units panel';
  grid-template-rows: auto 1fr;
  gap: var(--t-spacing-lg);
  align-items: start;

  @include screen-max-lg {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'stats'
      'panel'
      'units';
    grid-template-rows: none;
    gap: var(--t-spacing-md);
  }
}

.stats {
  grid-area: stats;
}

.units {
  grid-area: units;
  min-width: 0;
}

// It stays in view beside a long list of units, and scrolls on its own if it outgrows the window
.panel {
  grid-area: panel;
  position: sticky;
  top: var(--t-spacing-md);
  max-height: calc(100vh - 2 * var(--t-spacing-md));
  overflow: auto;

  @include screen-max-lg {
    position: static;
    max-height: none;
  }
}

// On a phone the filter takes a row and the sort and view controls the next
.tools {
  @include mobile {
    flex-direction: column;
    align-items: stretch;
  }
}

// The properties page's grid, so a unit's card looks the same in both places
.unit-cards {
  display: grid;
  gap: var(--t-spacing-md);
  grid-template-columns: repeat(auto-fill, minmax(22rem, 1fr));

  @include screen-max-sm {
    grid-template-columns: 1fr;
  }
}
</style>
